import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { sendCustomerNotice } from '../services/adminService';
import { labelStyle, inputStyle } from './adminFieldStyles';
import { NOTICE_TEMPLATES } from './customerNoticeTemplates';

// Private bucket (migration 085); the API attaches the images and deletes them after sending
export const NOTICE_BUCKET = 'customer_notice_attachments';
export const MAX_ATTACHMENTS = 2;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_BYTES = 5 * 1024 * 1024;
const SUBJECT_MAX = 150;
const BODY_MAX = 5000;
// Same rule as the API: one plain address, no lists
const EMAIL_RE = /^[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>"]+$/;
const EXT_BY_TYPE = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif' };

const buttonStyle = {
  padding: '0.55rem 1.25rem',
  borderRadius: '8px',
  fontSize: '0.9rem',
  fontWeight: 600,
  cursor: 'pointer',
};

/**
 * Admin → one customer: a one-off notice email with subject, message and up
 * to two image attachments. Not for promotions — those go through the
 * mailing list, which only reaches people who opted in.
 *
 * Without a `customer` the admin types the address (and optionally a name),
 * for people who aren't customers yet.
 */
const CustomerNoticeModal = ({ customer, accessToken, onClose, onSent }) => {
  const [toEmail, setToEmail] = useState('');
  const [toName, setToName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviewUrls(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !sending) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, sending]);

  const handleFile = (e) => {
    setError(null);
    const picked = e.target.files?.[0] || null;
    e.target.value = '';
    if (!picked) return;
    if (!ACCEPTED_TYPES.includes(picked.type)) {
      setError('La imagen debe ser JPG, PNG, WebP o GIF.');
      return;
    }
    if (picked.size > MAX_BYTES) {
      setError('La imagen no puede pesar más de 5 MB.');
      return;
    }
    setFiles((prev) => [...prev, picked].slice(0, MAX_ATTACHMENTS));
  };

  const applyTemplate = (e) => {
    const template = NOTICE_TEMPLATES.find((t) => t.id === e.target.value);
    if (!template) return;
    if ((subject.trim() || body.trim()) && !window.confirm('¿Reemplazar el asunto y el mensaje con la plantilla?')) return;
    setSubject(template.subject);
    setBody(template.body);
    setError(null);
  };

  const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const handleSend = async (e) => {
    e.preventDefault();
    setError(null);
    const recipient = customer ? customer.email : toEmail.trim().toLowerCase();
    if (!customer && !EMAIL_RE.test(recipient)) return setError('Escribe un solo correo válido.');
    if (!subject.trim()) return setError('Escribe el asunto.');
    if (!body.trim()) return setError('Escribe el mensaje.');
    if (!window.confirm(`¿Enviar este correo a ${recipient}?`)) return;

    setSending(true);
    const attachmentPaths = [];
    try {
      for (const file of files) {
        const path = `${Date.now()}_${Math.random().toString(36).slice(2)}.${EXT_BY_TYPE[file.type]}`;
        const { error: uploadErr } = await supabase.storage
          .from(NOTICE_BUCKET)
          .upload(path, file, { contentType: file.type, upsert: false });
        if (uploadErr) throw new Error(`No se pudo subir la imagen: ${uploadErr.message}`);
        attachmentPaths.push(path);
      }
      const to = customer ? { customerId: customer.id } : { email: recipient, name: toName.trim() };
      await sendCustomerNotice(accessToken, { ...to, subject: subject.trim(), body, attachmentPaths });
      onSent?.(customer || { email: recipient, name: toName.trim() });
    } catch (err) {
      // Not sent: don't leave uploaded images behind
      if (attachmentPaths.length) {
        try { await supabase.storage.from(NOTICE_BUCKET).remove(attachmentPaths); } catch { /* best effort */ }
      }
      setError(err.message || 'No se pudo enviar el correo.');
      setSending(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-notice-title"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000 }}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !sending) onClose(); }}
    >
      <form
        onSubmit={handleSend}
        noValidate
        style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' }}
      >
        <h2 id="customer-notice-title" style={{ margin: '0 0 0.25rem', fontSize: '1.2rem', color: '#333' }}>Enviar correo</h2>
        {customer ? (
          <p style={{ margin: '0 0 1.25rem', color: '#666', fontSize: '0.9rem', wordBreak: 'break-all' }}>
            Para: {customer.name ? `${customer.name} · ` : ''}{customer.email}
          </p>
        ) : (
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', margin: '0.75rem 0 1rem' }}>
            <div style={{ flex: '1 1 220px' }}>
              <label htmlFor="notice-to-email" style={labelStyle}>Para (correo)</label>
              <input
                id="notice-to-email"
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                maxLength={254}
                autoComplete="off"
                disabled={sending}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <label htmlFor="notice-to-name" style={labelStyle}>Nombre (opcional)</label>
              <input
                id="notice-to-name"
                type="text"
                value={toName}
                onChange={(e) => setToName(e.target.value)}
                maxLength={100}
                autoComplete="off"
                disabled={sending}
                style={inputStyle}
              />
            </div>
          </div>
        )}

        {NOTICE_TEMPLATES.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="notice-template" style={labelStyle}>Plantilla (opcional)</label>
            <select id="notice-template" value="" onChange={applyTemplate} disabled={sending} style={inputStyle}>
              <option value="">Elegir plantilla…</option>
              {NOTICE_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
        )}

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="notice-subject" style={labelStyle}>Asunto</label>
          <input
            id="notice-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={SUBJECT_MAX}
            disabled={sending}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="notice-body" style={labelStyle}>Mensaje</label>
          <textarea
            id="notice-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={BODY_MAX}
            rows={8}
            disabled={sending}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
          />
          <small style={{ display: 'block', marginTop: '0.25rem', color: '#888' }}>
            El asunto va como título y el correo empieza con "¡Hola, {((customer ? customer.name : toName) || '').trim().split(/\s+/)[0] || '…'}!". Deja una línea en blanco entre párrafos. Formato: <code>## Subtítulo</code>, <code>- elemento</code> para listas, <code>**negritas**</code>, <code>! aviso</code> (recuadro amarillo), <code>&gt; nota</code> (recuadro azul). {body.length}/{BODY_MAX}
          </small>
        </div>

        <div style={{ marginBottom: '1.25rem' }}>
          <span style={labelStyle}>Imágenes adjuntas (opcional, máx. {MAX_ATTACHMENTS})</span>
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              {previewUrls[i] && <img src={previewUrls[i]} alt={`Vista previa de la imagen ${i + 1}`} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }} />}
              <span style={{ fontSize: '0.85rem', color: '#555', wordBreak: 'break-all', flex: 1 }}>{f.name}</span>
              <button type="button" onClick={() => removeFile(i)} disabled={sending} aria-label={`Quitar imagen ${i + 1}`} style={{ ...buttonStyle, background: 'none', border: '1px solid #ddd', color: '#555' }}>
                Quitar
              </button>
            </div>
          ))}
          {files.length < MAX_ATTACHMENTS && (
            <input
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              onChange={handleFile}
              disabled={sending}
              aria-label={files.length ? 'Agregar otra imagen' : 'Imagen adjunta'}
            />
          )}
          <small style={{ display: 'block', marginTop: '0.25rem', color: '#888' }}>JPG, PNG, WebP o GIF · máx. 5 MB cada una</small>
        </div>

        {error && <p role="alert" style={{ color: '#dc3545', fontSize: '0.9rem', margin: '0 0 1rem' }}>{error}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button type="button" onClick={onClose} disabled={sending} style={{ ...buttonStyle, background: 'white', border: '1px solid #ddd', color: '#333' }}>
            Cancelar
          </button>
          <button type="submit" disabled={sending} style={{ ...buttonStyle, background: '#1a1a1a', border: '1px solid #1a1a1a', color: 'white' }}>
            {sending ? 'Enviando…' : 'Enviar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerNoticeModal;
